/**
 * MemberReassignmentDialog
 * Shown when removing project members who still have task RACI assignments.
 * User must pick one remaining team member as replacement per removed user.
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { UserMinus } from 'lucide-react';
import type { User } from '@/types';

export interface PendingRemoval {
  userId: string;
  taskCount: number;
}

interface MemberReassignmentDialogProps {
  open: boolean;
  pendingRemovals: PendingRemoval[];
  replacements: Record<string, string>;
  onReplacementsChange: (next: Record<string, string>) => void;
  candidateUserIds: string[];
  usersById: Map<string, User>;
  errorMessage: string | null;
  onClearError: () => void;
  onBack: () => void;
  onConfirm: () => void;
  isSubmitting: boolean;
}

function displayName(user: User | undefined, fallbackId: string): string {
  if (!user) return fallbackId;
  return (
    user.displayName ||
    [user.firstName, user.lastName].filter(Boolean).join(' ') ||
    user.email ||
    fallbackId
  );
}

const MemberReassignmentDialog: React.FC<MemberReassignmentDialogProps> = ({
  open,
  pendingRemovals,
  replacements,
  onReplacementsChange,
  candidateUserIds,
  usersById,
  errorMessage,
  onClearError,
  onBack,
  onConfirm,
  isSubmitting,
}) => {
  const canConfirm =
    !isSubmitting &&
    pendingRemovals.every((r) => Boolean(replacements[r.userId]));

  return (
    <Dialog
      open={open}
      onClose={() => !isSubmitting && onBack()}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <UserMinus size={24} />
          <Typography variant="h6">Reassign Task Ownership</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          These members have task assignments. Pick one replacement each so tasks
          are not left without an owner, then they will be removed from the project.
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={onClearError}>
            {errorMessage}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {pendingRemovals.map((item) => {
            const removedName = displayName(usersById.get(item.userId), item.userId);
            return (
              <Box key={item.userId}>
                <Typography variant="subtitle2" gutterBottom>
                  {removedName}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                  {item.taskCount} task assignment{item.taskCount !== 1 ? 's' : ''} will
                  move to the replacement
                </Typography>
                <FormControl fullWidth size="small">
                  <InputLabel id={`replacement-${item.userId}`}>
                    Replacement member
                  </InputLabel>
                  <Select
                    labelId={`replacement-${item.userId}`}
                    label="Replacement member"
                    value={replacements[item.userId] || ''}
                    onChange={(e) =>
                      onReplacementsChange({
                        ...replacements,
                        [item.userId]: e.target.value,
                      })
                    }
                  >
                    {candidateUserIds
                      .filter((id) => id !== item.userId)
                      .map((id) => (
                        <MenuItem key={id} value={id}>
                          {displayName(usersById.get(id), id)}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Box>
            );
          })}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onBack} color="inherit" disabled={isSubmitting}>
          Back
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          disabled={!canConfirm}
          startIcon={isSubmitting ? <CircularProgress size={16} /> : undefined}
        >
          {isSubmitting ? 'Reassigning...' : 'Reassign & Remove'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MemberReassignmentDialog;
